/**
 * State hook for SmswiserCampaignStatsTab.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useReportingCurrency } from '@/lib/reporting-currency';

import {
  dmyToIso,
  isoToDmy,
  todayIsoDateParis,
} from '@/features/smswiser-campaigns/utils/dateFormat';
import {
  normalizeCurrencyCode,
} from '@/features/smswiser-campaigns/utils/currency';
import type { GenericStatsRow } from '../../shared/generic-stats.types';
import { LENGTH_OPTIONS } from '../../shared/constants';
import type { GroupByKey } from '../../shared/constants';
import {
  cycleStatsSort,
  filtersToParams,
  parseFiltersFromParams,
  parseSortFromParams,
  sortToParams,
} from '../../shared';
import type { FilterConfig, FiltersState, SortState } from '../../shared';
import {
  getFilterFieldsForVariant,
  getGroupByOptionsForVariant,
  getDefaultSortForVariant,
  getDefaultGroupByForVariant,
  isImpressionsDefaultVariant,
} from './statsTabVariantConfig';
import type { StatsTabVariant } from './useStatsTabState.types';

export type { FilterConfig, FiltersState, StatsTabVariant };

const DEFAULT_LIMIT = 300;

function parseLimit(raw: string | null, variant: StatsTabVariant): number {
  const trimmed = raw?.trim();
  if (variant === 'phone' && (trimmed === 'all' || trimmed === '0')) return 0;
  const n = trimmed ? parseInt(trimmed, 10) : NaN;
  if (!Number.isFinite(n) || n < 1) return DEFAULT_LIMIT;
  return (LENGTH_OPTIONS as readonly number[]).includes(n) ? n : DEFAULT_LIMIT;
}

export function useStatsTabState(workDate: string, variant: StatsTabVariant = 'drops') {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const limitParam = searchParams.get('limit');
  const currencyParam = searchParams.get('currency');
  const groupByParam = searchParams.get('groupBy');
  const sortByParam = searchParams.get('sortBy');
  const sortDirParam = searchParams.get('sortDir');

  const filterFields = getFilterFieldsForVariant(variant);
  const groupByOptions = getGroupByOptionsForVariant(variant);
  const isImpressionsDefault = isImpressionsDefaultVariant(variant);
  const defaultFilter = useMemo<FilterConfig>(
    () => ({
      field: isImpressionsDefault ? 'impressions' : 'leadList',
      operator: '>=',
      value: isImpressionsDefault ? '1' : '',
    }),
    [isImpressionsDefault],
  );
  const parsedFilters = useMemo(
    () => parseFiltersFromParams((k) => searchParams.get(k), defaultFilter),
    [searchParams, defaultFilter],
  );
  const defaultSort = useMemo(() => getDefaultSortForVariant(variant), [variant]);
  const parsedSort = useMemo(
    () => parseSortFromParams(sortByParam, sortDirParam, defaultSort.field, defaultSort.dir),
    [sortByParam, sortDirParam, defaultSort.field, defaultSort.dir],
  );

  const validGroupByKeys = new Set(groupByOptions.map((o) => o.key));
  const defaultGroupBy = getDefaultGroupByForVariant(variant);
  const parsedGroupBy: GroupByKey = (validGroupByKeys.has(groupByParam ?? '')
    ? groupByParam
    : defaultGroupBy) as GroupByKey;

  // The reporting currency follows /app-env even when this tab opened before it answered.
  const reporting = useReportingCurrency();
  const parsedCurrency = currencyParam ? normalizeCurrencyCode(currencyParam) : reporting;

  const fromIso = fromParam ? dmyToIso(fromParam) || workDate : workDate;
  const toIso = toParam ? dmyToIso(toParam) || todayIsoDateParis() : todayIsoDateParis();

  const [fromDate, setFromDateState] = useState(fromIso);
  const [toDate, setToDateState] = useState(toIso);
  const [length, setLengthState] = useState<number>(() => parseLimit(limitParam, variant));
  const [currency, setCurrencyState] = useState<string>(parsedCurrency);

  useEffect(() => {
    if (fromParam) {
      const iso = dmyToIso(fromParam);
      if (iso) setFromDateState(iso);
    }
  }, [fromParam]);
  useEffect(() => {
    if (toParam) {
      const iso = dmyToIso(toParam);
      if (iso) setToDateState(iso);
    }
  }, [toParam]);
  useEffect(() => {
    if (limitParam != null) setLengthState(parseLimit(limitParam, variant));
  }, [limitParam, variant]);
  useEffect(() => {
    if (currencyParam) setCurrencyState(normalizeCurrencyCode(currencyParam));
    else setCurrencyState(reporting);
  }, [currencyParam, reporting]);
  useEffect(() => {
    setFiltersState(parsedFilters);
  }, [parsedFilters]);
  useEffect(() => {
    setSortState(parsedSort);
  }, [parsedSort]);
  useEffect(() => {
    setGroupByState(parsedGroupBy);
  }, [groupByParam]);

  const setFromDate = useCallback(
    (v: string) => {
      setFromDateState(v);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('from', isoToDmy(v));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setToDate = useCallback(
    (v: string) => {
      setToDateState(v);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('to', isoToDmy(v));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  /** Updates both from and to in one URL update — use when changing presets to avoid stale data. */
  const setDateRange = useCallback(
    (from: string, to: string) => {
      setFromDateState(from);
      setToDateState(to);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('from', isoToDmy(from));
          next.set('to', isoToDmy(to));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setLength = useCallback(
    (v: number) => {
      setLengthState(v);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('limit', String(v));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setCurrency = useCallback(
    (code: string) => {
      const normalized = normalizeCurrencyCode(code);
      setCurrencyState(normalized);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('currency', normalized);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [filtersState, setFiltersState] = useState<FiltersState>(parsedFilters);
  const shouldSyncFiltersToUrl = useRef(false);
  const [sort, setSortState] = useState<SortState>(parsedSort);
  const [groupBy, setGroupByState] = useState<GroupByKey>(parsedGroupBy);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [selectedRows, setSelectedRows] = useState<GenericStatsRow[]>([]);

  const setGroupBy = useCallback(
    (v: GroupByKey) => {
      setGroupByState(v);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === 'none') next.delete('groupBy');
          else next.set('groupBy', v);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const [showScaleModal, setShowScaleModal] = useState(false);
  const [showCopyAsDraftModal, setShowCopyAsDraftModal] = useState(false);

  useEffect(() => {
    setSelectedRows([]);
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!shouldSyncFiltersToUrl.current) return;
    shouldSyncFiltersToUrl.current = false;
    const params = filtersToParams(filtersState);
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        ['filterActive', 'filterField', 'filterOp', 'filterValue'].forEach((k) => next.delete(k));
        for (let i = 0; i < 10; i++) {
          next.delete(`filter${i}Field`);
          next.delete(`filter${i}Op`);
          next.delete(`filter${i}Value`);
        }
        Object.entries(params).forEach(([k, v]) => next.set(k, v));
        return next;
      },
      { replace: true },
    );
  }, [filtersState, setSearchParams]);

  // NOTE: we intentionally do NOT seed from/to/limit/currency into the URL on
  // mount. A clean URL (e.g. /smswiser/australia) means "today + defaults"; only
  // an explicit change (via the setters below) writes a param. The data fetch
  // uses the hook's state, which is already initialised from the URL/defaults.

  const handleSortClick = useCallback(
    (field: string) => {
      const resolved = cycleStatsSort(sort, field, defaultSort.field, defaultSort.dir);
      const params = sortToParams(resolved);
      setSearchParams(
        (sp) => {
          const nextSp = new URLSearchParams(sp);
          if (params.sortBy) nextSp.set('sortBy', params.sortBy);
          else nextSp.delete('sortBy');
          if (params.sortDir) nextSp.set('sortDir', params.sortDir);
          else nextSp.delete('sortDir');
          return nextSp;
        },
        { replace: true },
      );
      setSortState(resolved);
    },
    [sort, setSearchParams, defaultSort.field, defaultSort.dir],
  );

  const setFilters = useCallback((updater: (prev: FiltersState) => FiltersState) => {
    shouldSyncFiltersToUrl.current = true;
    setFiltersState((prev) => updater(prev));
  }, []);

  return {
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    setDateRange,
    length,
    setLength,
    currency,
    groupBy,
    setGroupBy,
    setCollapsedGroups,
    collapsedGroups,
    toggleGroup,
    setCurrency,
    filtersState,
    setFilters,
    defaultFilter,
    sort,
    handleSortClick,
    selectedRows,
    setSelectedRows,
    showScaleModal,
    setShowScaleModal,
    showCopyAsDraftModal,
    setShowCopyAsDraftModal,
    selectedFieldType:
      filterFields.find((f) => f.key === filtersState.filters[0]?.field)?.type ?? 'numeric',
  };
}
